import { type Block, type Config, type StoryPage, TypeEnum, type Uid } from "@/react/story/types";

class InitUtilsWrapper {

    uid(): Uid {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === "x" ? r : (r & 0x3 | 0x8);

            return v.toString(16);
        });
    }

    block(lastBlock: Block | undefined): Block {
        return {
            "#type": TypeEnum.BLOCK,
            uid: this.uid(),
            config: lastBlock?.config || this.config(),
            contents: [],
            separator: null,
        };
    }

    config(): Config {
        return {
            _: new Date().toISOString(),
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- recursive deep-clone over arbitrary JSON ODM payloads
    withNewUids<T = any>(item: T): T {
        // tout ce qui porte un uid vit dans un tableau : les contenus d'un bloc, les polices
        // d'une story. Sans cette branche, la copie partage le tableau de la source.
        if (Array.isArray(item)) {
            return item.map(v => this.withNewUids(v)) as T;
        }

        // une date n'a pas de clés à parcourir : la traverser comme un objet la viderait
        if (item instanceof Date) {
            return new Date(item.getTime()) as T;
        }

        if ("object" === typeof item && null !== item) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- accumulator for arbitrary JSON ODM keys
            const o: Record<string, any> = {};
            const source = item as Record<string, unknown>;
            Object.keys(item).forEach(k => {
                // backend keys
                if ([ "@context", "@type", "@id", "id" ].includes(k)) {
                    return;
                }

                // some keys must be reset
                if ("_" === k) {
                    o[k] = new Date().toISOString();
                } else if ("uid" === k) {
                    o[k] = this.uid();
                } else {
                    o[k] = this.withNewUids(source[k]);
                }
            });

            return o as T;
        }

        return item;
    }

    findColors(o: StoryPage[] | StoryPage) {
        const entries = new Map();
        this.findDeepColors(o).forEach(color => {
            entries.set(color, 1 + (entries.get(color) ?? 0));
        });

        const sorted = [ ...entries.entries() ]
            .sort((a, b) => b[1] - a[1])
            .map(a => a[0]);

        return [ ...sorted ];
    }

    findDeepColors(o: unknown) {
        const colors: string[] = [];
        if (Array.isArray(o)) {
            o.forEach(v => {
                colors.push(...this.findDeepColors(v));
            });
        } else if ("object" === typeof o && null !== o) {
            const source = o as Record<string, unknown>;
            Object.keys(source).forEach(k => {
                if ("color" === k || "backgroundColor" === k) {
                    const color = source[k];
                    if ("string" === typeof color && /^#[a-f0-9]{6}$/i.test(color)) {
                        colors.push(color.toLowerCase());
                    }
                } else {
                    colors.push(...this.findDeepColors(source[k]));
                }
            });
        }

        return colors;
    }

    setDeepValue(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- walks/mutates an arbitrary nested JSON ODM object
        obj: Record<string, any>,
        path: string,
        value: unknown,
    ): void {
        const keys = path.split(".");

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- cursor into the arbitrary nested object above
        let current: Record<string, any> = obj;

        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];

            if (
                undefined === current[key]
                || null === current[key]
                || "object" !== typeof current[key]
            ) {
                current[key] = {};
            }

            current = current[key];
        }

        current[keys[keys.length - 1]] = value;
    }

}

const InitUtils = new InitUtilsWrapper();

export default InitUtils;
